import { Typography, TypographyVariants, TypographyColors } from '@carboledger/ui';
import { UNIT_TYPES } from '@/config/unitTypes.constants';
import { InfoLabel } from '../components/InfoLabel';
import { URGENT_DAYS } from './allocations.constants';

// The Overview tab's three FieldSection columns.

const certifiedQuantityField = (t, allocation, unitType) => ({
  id: `certified-${unitType}`,
  label: <InfoLabel>{t('ALLOCATIONS_COL_CERTIFIED_QUANTITY')}</InfoLabel>,
  value: allocation.byUnit?.[unitType]?.certifiedQuantity ?? '-',
});

// Red once the dispatch deadline is inside a week, matching the list's own column.
const daysLeftValue = (t, daysLeft) => {
  if (daysLeft == null) return '-';
  return (
    <Typography
      variant={TypographyVariants.bodySm}
      color={daysLeft < URGENT_DAYS ? TypographyColors.error : TypographyColors.primary}
    >
      {t('ALLOCATIONS_DAYS_LEFT', { days: daysLeft })}
    </Typography>
  );
};

/**
 * One quantity block per unit basis. Figma warns under working and tracking when the linked
 * inbounds carry no density to convert with, but no DTO field says so — hence no footer.
 */
export const buildAllocationQuantitySections = (t, allocation) => [
  {
    heading: t('SECTION_QUANTITY_IN_WORKING_UNIT'),
    fields: [certifiedQuantityField(t, allocation, UNIT_TYPES.working)],
  },
  {
    heading: t('SECTION_QUANTITY_IN_DECLARATION_UNIT'),
    fields: [certifiedQuantityField(t, allocation, UNIT_TYPES.declaration)],
  },
  // TODO: the fulfillment DTO carries no tracking-unit quantity.
  {
    heading: t('SECTION_QUANTITY_IN_TRACKING_UNIT'),
    fields: [
      {
        id: 'certified-tracking',
        label: <InfoLabel>{t('ALLOCATIONS_COL_CERTIFIED_QUANTITY')}</InfoLabel>,
        value: '-',
      },
    ],
  },
];

export const buildAllocationAdditionalFields = (t, allocation) => [
  {
    id: 'dateOfDispatch',
    label: <InfoLabel>{t('FIELD_DATE_OF_DISPATCH')}</InfoLabel>,
    value: allocation.dateOfDispatch,
  },
  {
    id: 'daysLeft',
    label: <InfoLabel>{t('FIELD_DAYS_LEFT')}</InfoLabel>,
    value: daysLeftValue(t, allocation.daysLeft),
  },
  {
    id: 'massBalancePeriod',
    label: <InfoLabel>{t('ALLOCATIONS_COL_MASS_BALANCE_PERIOD')}</InfoLabel>,
    value: allocation.massBalancePeriod,
  },
  {
    id: 'reference',
    label: <InfoLabel>{t('ALLOCATIONS_COL_REFERENCE')}</InfoLabel>,
    value: allocation.reference,
  },
  {
    id: 'associatedDeclarationNo',
    label: <InfoLabel>{t('ALLOCATIONS_COL_ASSOCIATED_DECLARATION_NO')}</InfoLabel>,
    value: allocation.associatedDeclarationNo,
  },
  { id: 'transactedBy', label: t('ALLOCATIONS_COL_TRANSACTED_BY'), value: allocation.transactedBy },
];

export const buildAllocationPreferenceFields = (t, allocation) => [
  // ORDERS_-prefixed but already shared with outgoing shipments; the field is the same one.
  { id: 'maxGhg', label: t('ORDERS_COL_MAX_GHG_EMISSIONS'), value: allocation.ghgEmissions },
  {
    id: 'minGhgSavings',
    label: <InfoLabel>{t('ORDERS_COL_MIN_GHG_SAVINGS')}</InfoLabel>,
    value: allocation.ghgEmissionSavings,
  },
  {
    id: 'rawMaterialType',
    label: t('FIELD_RAW_MATERIAL_TYPE'),
    value: allocation.rawMaterialType,
  },
  {
    id: 'countryOfOrigin',
    label: t('FIELD_COUNTRY_OF_ORIGIN'),
    value: allocation.countryOfOrigin,
  },
  {
    id: 'feedstockCategory',
    label: <InfoLabel>{t('FIELD_FEEDSTOCK_CATEGORY')}</InfoLabel>,
    value: allocation.feedstockCategory,
  },
  {
    id: 'allocatedQuota',
    label: <InfoLabel>{t('ALLOCATIONS_COL_ALLOCATED_QUOTA')}</InfoLabel>,
    value: allocation.allocatedQuota,
  },
];
